import React, { useEffect, useRef, useState } from "react";
import { Package, Download, LogOut, ShoppingBag, FileText, Pencil, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/lib/helpers";
import MyBusiness from "@/components/MyBusiness";
import { toast } from "sonner";

const Account = () => {
  const { t, lang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cvs, setCvs] = useState([]);
  const [tplMap, setTplMap] = useState({});
  const [previews, setPreviews] = useState({});
  const previewsRef = useRef({});

  const loadPreviews = async (list) => {
    const entries = await Promise.all(
      list.filter((c) => c.pdf_ready).map(async (c) => {
        try {
          const res = await api.get(`/cv/${c.id}/preview`, { responseType: "blob" });
          return [c.id, URL.createObjectURL(res.data)];
        } catch { return null; }
      })
    );
    const map = Object.fromEntries(entries.filter(Boolean));
    Object.values(previewsRef.current).forEach((u) => URL.revokeObjectURL(u));
    previewsRef.current = map;
    setPreviews(map);
  };

  useEffect(() => () => { Object.values(previewsRef.current).forEach((u) => URL.revokeObjectURL(u)); }, []);

  useEffect(() => {
    api.get("/orders/my").then(({ data }) => setOrders(data)).finally(() => setLoading(false));
    api.get("/cv/mine").then(({ data }) => { setCvs(data); loadPreviews(data); }).catch(() => {});
    api.get(`/cv/templates?lang=${lang}`).then(({ data }) => {
      const m = {}; data.forEach((x) => { m[x.id] = x; }); setTplMap(m);
    }).catch(() => {});
  }, [lang]);

  const downloadCv = async (id, ext) => {
    try {
      const path = ext === "docx" ? `/cv/${id}/download-docx` : `/cv/${id}/download`;
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `cv-learnspherehub.${ext}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download error"); }
  };

  const payCv = async (id) => {
    try {
      const { data } = await api.post(`/cv/${id}/checkout`, { origin_url: window.location.origin });
      if (data.already_paid) { toast.success(t("cv.paidSuccess")); setCvs((cs) => cs.map((c) => c.id === id ? { ...c, paid: true } : c)); return; }
      window.location.href = data.checkout_url;
    } catch { toast.error("Error"); }
  };

  const download = async (productId, title) => {
    try {
      const res = await api.get(`/products/${productId}/download`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `${title}.txt`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Download error");
    }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div data-testid="account-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm text-brand-navy/50">{t("account.welcome")}</p>
          <h1 className="text-3xl font-extrabold font-heading text-brand-navy tracking-tight">{user?.name || user?.email}</h1>
        </div>
        <Button data-testid="account-logout" onClick={doLogout} variant="outline" className="rounded-full text-brand-navy border-brand-navy/20 gap-2">
          <LogOut className="h-4 w-4" /> {t("nav.logout")}
        </Button>
      </div>

      <h2 className="mt-10 text-xl font-bold font-heading text-brand-navy flex items-center gap-2"><Package className="h-5 w-5 text-brand-electric" /> {t("account.orders")}</h2>

      {loading ? (
        <div className="py-16 text-center text-brand-navy/50">{t("common.loading")}</div>
      ) : orders.length === 0 ? (
        <div data-testid="account-no-orders" className="mt-6 rounded-2xl border border-brand-navy/10 bg-white p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-brand-navy/5 flex items-center justify-center text-brand-navy/40"><ShoppingBag className="h-7 w-7" /></div>
          <p className="mt-4 text-brand-navy/60">{t("account.noOrders")}</p>
          <Button onClick={() => navigate("/prodotti")} className="mt-5 rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white">{t("cart.emptyCta")}</Button>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="rounded-2xl border border-brand-navy/10 bg-white p-6">
              <div className="flex items-center justify-between border-b border-brand-navy/10 pb-3">
                <span className="text-sm text-brand-navy/50">{t("account.order")} #{o.id.slice(-6)}</span>
                <span className="font-heading font-bold text-brand-navy">{formatPrice(o.total)}</span>
              </div>
              <div className="mt-3 space-y-3">
                {o.items.map((it, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-brand-navy truncate">{it.title}</p>
                      <p className="text-xs text-brand-navy/50">{t("cart.qty")}: {it.quantity} · {formatPrice(it.price)}</p>
                    </div>
                    {String(it.title || "").toUpperCase().includes("RESTART DATING") ? (
                      <a data-testid={`goto-guided-${it.product_id}`} href="/percorsi/restart-dating-45"
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white text-sm font-medium px-4 py-2 shrink-0">
                        <Download className="h-4 w-4" /> Accedi ai contenuti
                      </a>
                    ) : (
                    <Button data-testid={`download-${it.product_id}`} onClick={() => download(it.product_id, it.title)} size="sm"
                      className="rounded-full bg-brand-orange hover:bg-brand-orange/90 text-white gap-1.5 shrink-0">
                      <Download className="h-4 w-4" /> {t("common.download")}
                    </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* I miei CV */}
      <h2 className="mt-12 text-xl font-bold font-heading text-brand-navy flex items-center gap-2"><Sparkles className="h-5 w-5 text-brand-orange" /> {t("account.myCvs")}</h2>
      {cvs.length === 0 ? (
        <div data-testid="account-no-cvs" className="mt-6 rounded-2xl border border-brand-navy/10 bg-white p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-brand-orange/10 flex items-center justify-center text-brand-orange"><FileText className="h-7 w-7" /></div>
          <p className="mt-4 text-brand-navy/60">{t("account.noCvs")}</p>
          <Button data-testid="account-cv-cta" onClick={() => navigate("/servizi-smart/cv-smart-premium")} className="mt-5 rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white">CV Smart Premium</Button>
        </div>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="account-cvs">
          {cvs.map((c) => (
            <div key={c.id} data-testid={`account-cv-${c.id}`} className="rounded-2xl border border-brand-navy/10 bg-white overflow-hidden flex flex-col">
              <div className="flex gap-3 p-4 border-b border-brand-navy/5">
                <div className="h-24 w-16 rounded-md overflow-hidden border border-brand-navy/10 bg-brand-light shrink-0">
                  {previews[c.id]
                    ? <img data-testid={`account-cv-thumb-${c.id}`} src={previews[c.id]} alt={c.name || c.title} className="w-full h-full object-contain" />
                    : tplMap[c.template]?.preview_path
                      ? <img src={`${API}/media/${tplMap[c.template].preview_path}`} alt="" className="w-full h-full object-contain" />
                      : <div className="w-full h-full flex items-center justify-center text-brand-navy/20"><FileText className="h-6 w-6" /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-heading font-bold text-brand-navy truncate">{c.title}</p>
                    {c.paid
                      ? <span data-testid={`account-cv-paid-${c.id}`} className="text-[10px] font-bold text-green-700 bg-green-50 rounded-full px-1.5 py-0.5 shrink-0">{t("cv.paidBadge")}</span>
                      : <span className="text-[10px] font-bold text-brand-orange bg-brand-orange/10 rounded-full px-1.5 py-0.5 shrink-0">{t("cv.watermarkBadge")}</span>}
                  </div>
                  {c.name && <p data-testid={`account-cv-name-${c.id}`} className="text-xs font-semibold text-brand-navy/70 truncate">{c.name}</p>}
                  <p className="text-xs text-brand-navy/50 mt-0.5">{tplMap[c.template]?.name || c.template}</p>
                  <p className="text-xs text-brand-navy/40 mt-0.5">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""} · {c.language?.toUpperCase()}</p>
                </div>
              </div>
              <div className="p-3 flex items-center gap-2 flex-wrap">
                <Button data-testid={`account-cv-edit-${c.id}`} onClick={() => navigate(`/servizi-smart/cv-smart-premium?edit=${c.id}`)} size="sm" variant="outline" className="rounded-full text-brand-navy border-brand-navy/20 gap-1.5"><Pencil className="h-3.5 w-3.5" /> {t("cv.edit")}</Button>
                {c.paid ? (
                  c.pdf_ready && <Button data-testid={`account-cv-pdf-${c.id}`} onClick={() => downloadCv(c.id, "pdf")} size="sm" className="rounded-full bg-brand-orange hover:bg-brand-orange/90 text-white gap-1.5"><Download className="h-3.5 w-3.5" /> PDF</Button>
                ) : (
                  <Button data-testid={`account-cv-unlock-${c.id}`} onClick={() => payCv(c.id)} size="sm" className="rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white gap-1.5"><Sparkles className="h-3.5 w-3.5" /> {t("cv.unlockMyCv")}</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <MyBusiness />
    </div>
  );
};

export default Account;
